import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const INTUITIVE_FILE_OF_TRUTH_CATEGORY_CONDITION_ID =
  'intuitiveFotCategory';
export const FilterConditionIntuitiveFileOfTruthCategoryPluginType: FilterConditionPluginSpecification<
  Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthCategoryPluginDto,
  IntuitiveFileOfTruthCategoryCondition
> = {
  type: 'intuitiveFotCategory',
  category: 'case',
  displayName: {
    de: 'Intuitive File of Truth Category',
    en: 'Intuitive File of Truth Category',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionIntuitiveFileOfTruthCategoryPlugin {
    return new FilterConditionIntuitiveFileOfTruthCategoryPlugin([], null);
  },
  fromCondition(
    jsonObj: IntuitiveFileOfTruthCategoryCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthCategoryPlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonIntuitiveFileOfTruthCategoryPluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthCategoryPlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionIntuitiveFileOfTruthCategoryPlugin extends FilterConditionPluginImpl<
  Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthCategoryPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'>,
    JsonIntuitiveFileOfTruthCategoryPluginDto,
    IntuitiveFileOfTruthCategoryCondition
  > = FilterConditionIntuitiveFileOfTruthCategoryPluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionIntuitiveFileOfTruthCategoryPluginType.type);
  }

  override equals(
    other: FilterConditionIntuitiveFileOfTruthCategoryPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionIntuitiveFileOfTruthCategoryPlugin {
    return new FilterConditionIntuitiveFileOfTruthCategoryPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new IntuitiveFileOfTruthCategoryCondition(this.includes, this.excludes);
  }

  toJSON(): JsonIntuitiveFileOfTruthCategoryPluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonIntuitiveFileOfTruthCategoryPluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (this.includes && this.includes.length > 0) || (this.excludes && this.excludes.length > 0);
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens eine Intuitive File of Truth Category auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Category auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Category auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'eine Intuitive File of Truth Category'
        : `${this.excludes.length} Intuitive File of Truth Categories`;
    }

    return this.includes.length === 1
      ? 'eine Intuitive File of Truth Category'
      : `${this.includes.length} Intuitive File of Truth Categories`;
  }
}

class IntuitiveFileOfTruthCategoryCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(INTUITIVE_FILE_OF_TRUTH_CATEGORY_CONDITION_ID, includes, excludes);
  }
}

interface JsonIntuitiveFileOfTruthCategoryPluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
